import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { articlesApi } from '../services/articlesApi';
import './Actualites.css';

const CATEGORIES = [
  { id: 'all',          lbl: 'Toutes',        cls: '',             color: null },
  { id: 'jeunesse',     lbl: 'Jeunesse',      cls: 'jeunesse',     color: 'var(--sunset)' },
  { id: 'spirituel',    lbl: 'Spirituel',     cls: 'spirituel',    color: 'var(--gold)' },
  { id: 'environnement',lbl: 'Environnement', cls: 'environnement',color: '#7DB3C5' },
  { id: 'sport',        lbl: 'Sport · ASC',   cls: 'sport',        color: 'var(--char-900)' },
  { id: 'culture',      lbl: 'Culture',       cls: 'culture',      color: '#E2CB91' },
  { id: 'mairie',       lbl: 'Mairie',        cls: 'mairie',       color: '#1B6985' },
];

const RUBRIQUES = [
  { cls: 'jeunesse',      title: 'Jeunesse',      desc: 'Initiatives, formations, collectifs — la jeunesse de Cambérène se raconte.' },
  { cls: 'spirituel',     title: 'Spirituel',     desc: 'Calendrier Niani, célébrations, organisation des cérémonies religieuses.' },
  { cls: 'sport',         title: 'Sport · ASC',   desc: "Matchs, classements, jeunes talents et coulisses de l'ASC Cambérène." },
  { cls: 'environnement', title: 'Environnement', desc: 'Plage propre, biodiversité côtière, mobilisation des habitants.' },
  { cls: 'culture',       title: 'Culture',       desc: 'Tassou, festivals, artisanat — la vie culturelle du village.' },
  { cls: 'mairie',        title: 'Mairie',        desc: 'Chantiers, budget participatif, conseil municipal — décisions publiques.' },
];

const VOICES = [
  { name: 'Aminata Diop', role: 'Rédactrice en chef', bio: 'Journaliste, native de Cambérène. Couvre la jeunesse depuis 2022.', count: '32 articles', seed: 'cb-voi-aminata' },
  { name: 'Cheikh L.',    role: 'Spiritualité',       bio: "Étudie les écrits Layène. Tient la chronique du pèlerinage annuel.", count: '21 articles', seed: 'cb-voi-cheikh' },
  { name: 'Mor Sène',     role: 'Photo · Sport',      bio: "Photographe résident, couvre l'ASC depuis 5 saisons.",               count: '48 photos',   seed: 'cb-voi-mor' },
  { name: 'Yacine Ba',    role: 'Environnement',      bio: 'Animatrice du collectif Plage Propre. Écrit le samedi.',             count: '14 articles', seed: 'cb-voi-yacine' },
];

const PHOTOWEEK = {
  title: 'Les pirogues qui rentrent.',
  desc:  'Plage Nord, 18h41. Trois pirogues bleues regagnent le rivage après la pêche du soir.',
  author:'Mor Sène',
  date:  'Photo du jour · 18.05.2026',
  seed:  'cb-photoweek-pirogues',
};

// Helpers — UI dérivée de l'article API
function getCatMeta(id) {
  return CATEGORIES.find((c) => c.id === id) || { id, lbl: id, cls: id };
}
function readTime(content) {
  const words = String(content || '').trim().split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 220));
  return `${mins} min`;
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}
function fmtDateLong(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}
function imageBg(article) {
  if (article.imageUrl) return `url('${article.imageUrl}')`;
  const seed = article.imageSeed || `cb-news-${article.slug}`;
  return `url('https://picsum.photos/seed/${seed}/1400/1000')`;
}

const Actualites = () => {
  const [filter, setFilter] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    articlesApi.list({ limit: 50 })
      .then((data) => { if (!cancelled) { setArticles(data.items); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.act-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  // Featured = premier article (le plus récent)
  const featured = articles[0] || null;
  const rest = articles.slice(1);

  const visibleArticles = useMemo(() => {
    if (filter === 'all') return rest;
    return rest.filter((a) => a.category === filter);
  }, [filter, rest]);

  // Compteurs par catégorie pour les chips
  const counts = useMemo(() => {
    const c = { all: articles.length };
    for (const a of articles) c[a.category] = (c[a.category] || 0) + 1;
    return c;
  }, [articles]);

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <main className="act-page">
      {/* ============== HERO ÉDITORIAL ============== */}
      <header className="act-hero">
        <div className="wrap">
          <div className="act-masthead">
            <span className="lab">Cambérène News</span>
            <span>Édition du <b>{today}</b></span>
            <span>N° <b>142</b> · Hebdomadaire</span>
          </div>

          <div className="act-hero-grid">
            <div className="act-hero-title reveal">
              <h1>Le journal<br />du <span className="ital">village.</span></h1>
              <p className="lede">
                Initiatives jeunesse, événements religieux, vie associative et sport.
                Ce qui se vit à Cambérène, raconté depuis Cambérène — par celles et ceux qui y vivent.
              </p>
              <div className="stats">
                <div><b>{loading ? '—' : articles.length}</b><span>Articles publiés</span></div>
                <div><b>{VOICES.length}+</b><span>Voix contributrices</span></div>
                <div><b>4 200</b><span>Lecteurs/semaine</span></div>
              </div>
            </div>

            {loading && (
              <div className="act-hero-feature reveal" style={{ alignItems: 'center', justifyContent: 'center', color: 'rgba(250,246,238,.5)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.2em' }}>
                Chargement…
              </div>
            )}

            {!loading && featured && (
              <Link to={`/actualites/${featured.slug}`} className="act-hero-feature reveal">
                <span className="badge">À la une</span>
                <div className="img"><div style={{ backgroundImage: imageBg(featured) }} /></div>
                <div className="body">
                  <span className="cat">{getCatMeta(featured.category).lbl}</span>
                  <h2>{featured.title}</h2>
                  <p>{featured.excerpt}</p>
                  <div className="byline">
                    <span>{fmtDateLong(featured.publishedAt)} · {featured.author}</span>
                    <span className="read">Lecture · {readTime(featured.content)} →</span>
                  </div>
                </div>
              </Link>
            )}

            {!loading && !featured && (
              <div className="act-hero-feature reveal" style={{ alignItems: 'center', justifyContent: 'center', color: 'rgba(250,246,238,.5)', textAlign: 'center', padding: 40 }}>
                <div>
                  <p style={{ fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--bone)', margin: 0 }}>Aucun article publié pour l'instant.</p>
                  <small style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', display: 'block', marginTop: 12 }}>Revenez bientôt — la prochaine édition se prépare.</small>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div style={{ marginTop: 18, padding: '10px 14px', border: '1px solid rgba(231,111,81,.4)', background: 'rgba(231,111,81,.08)', color: '#A8431E', borderRadius: 8, fontSize: 13 }}>
              Impossible de charger les actualités : {error}
            </div>
          )}
        </div>
      </header>

      {/* ============== FILTRES ============== */}
      <div className="act-filters">
        <div className="wrap">
          <span className="lab">Rubriques —</span>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`act-chip${filter === c.id ? ' on' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.color && <span className="dot" style={{ background: c.color }} />}
              {c.lbl}
              <span className="ct">{counts[c.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ============== ARTICLES GRID ============== */}
      <section className="act-section">
        <div className="wrap">
          <div className="act-head reveal">
            <div>
              <span className="eyebrow">{filter === 'all' ? 'Articles récents' : `Filtré · ${getCatMeta(filter).lbl}`}</span>
              <h2>Cette <span className="ital">semaine.</span></h2>
            </div>
            <Link to="#" className="seeall">Tous les articles →</Link>
          </div>

          {visibleArticles.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
              {loading ? 'Chargement…' : 'Aucun article pour cette rubrique pour l\'instant.'}
            </p>
          ) : (
            <div className="art-grid">
              {visibleArticles.map((a) => {
                const cat = getCatMeta(a.category);
                return (
                  <Link key={a.id} to={`/actualites/${a.slug}`} className="art-card reveal">
                    <div className="ph">
                      <div className="img" style={{ backgroundImage: imageBg(a) }} />
                      <span className={`cat-pill ${cat.cls}`}>{cat.lbl}</span>
                    </div>
                    <h3>{a.title}</h3>
                    <p>{a.excerpt}</p>
                    <div className="byline">
                      <span>{fmtDate(a.publishedAt)}</span><span className="sep">·</span>
                      <span>{a.author}</span><span className="sep">·</span>
                      <span>{readTime(a.content)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== PAR RUBRIQUE ============== */}
      <section className="act-section act-rubrique">
        <div className="wrap">
          <div className="act-head reveal">
            <div>
              <span className="eyebrow">Toutes les rubriques</span>
              <h2>Six fenêtres<br /><span className="ital">sur Cambérène.</span></h2>
            </div>
          </div>

          <div className="rub-grid">
            {RUBRIQUES.map((r, i) => (
              <button
                key={r.cls}
                type="button"
                className={`rub-card ${r.cls} reveal`}
                onClick={() => { setFilter(r.cls); window.scrollTo({ top: document.querySelector('.act-section').offsetTop - 60, behavior: 'smooth' }); }}
                style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
              >
                <div className="h">
                  <small>{String(i + 1).padStart(2, '0')}</small>
                  <b>{counts[r.cls] || 0}</b>
                </div>
                <h4>{r.title}</h4>
                <p>{r.desc}</p>
                <span className="arr">Voir tout →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PHOTO DE LA SEMAINE ============== */}
      <section className="act-section act-photoweek">
        <div className="wrap">
          <div className="act-head reveal" style={{ marginBottom: 24 }}>
            <div>
              <span className="eyebrow" style={{ color: 'rgba(250,246,238,.65)' }}>Photo de la semaine</span>
              <h2 style={{ color: 'var(--bone)' }}>L'instant<br /><span className="ital" style={{ color: 'var(--gold-soft)' }}>capturé.</span></h2>
            </div>
          </div>

          <div className="pw-shell reveal">
            <div className="pw-photo">
              <div className="img" style={{ backgroundImage: `url('https://picsum.photos/seed/${PHOTOWEEK.seed}/1800/1300')` }} />
              <span className="tag">{PHOTOWEEK.date}</span>
            </div>
            <div className="pw-body">
              <span className="eyebrow">Cliché · Mor Sène</span>
              <h3>« {PHOTOWEEK.title} »</h3>
              <p>{PHOTOWEEK.desc}</p>
              <div className="credit">
                <span className="av" />
                <span>{PHOTOWEEK.author} · Photographe résident</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== VOIX ============== */}
      <section className="act-section act-voices">
        <div className="wrap">
          <div className="act-head reveal">
            <div>
              <span className="eyebrow">Les voix de Cambérène</span>
              <h2>Qui raconte<br /><span className="ital">le village ?</span></h2>
            </div>
            <Link to="#" className="seeall">Devenir contributeur →</Link>
          </div>

          <div className="voi-grid">
            {VOICES.map((v) => (
              <article key={v.name} className="voi-card reveal">
                <div className="voi-av" style={{ backgroundImage: `url('https://picsum.photos/seed/${v.seed}/200/200')` }} />
                <span className="role">{v.role}</span>
                <h5>{v.name}</h5>
                <p>{v.bio}</p>
                <div className="meta">
                  <span>{v.count}</span>
                  <span>Lire →</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== NEWSLETTER ============== */}
      <section className="act-news">
        <div className="wrap reveal">
          <span className="eyebrow">Lettre du village</span>
          <h3>Recevez Cambérène<br /><span className="ital">dans votre boîte.</span></h3>
          <p>
            Chaque premier dimanche : un récit, une photo, les matchs de l'ASC, les portraits
            et l'agenda du mois. Sans spam, désabonnement en un clic.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); e.currentTarget.querySelector('button').textContent = 'Merci !'; }}>
            <input type="email" placeholder="votre@adresse.email" />
            <button type="submit">S'abonner</button>
          </form>
          <span className="fine">3 200 abonnés · 1 mail par mois · pas de tracking</span>
        </div>
      </section>
    </main>
  );
};

export default Actualites;
